import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Play, Users, Sparkles, ArrowRight } from "lucide-react";
import MetaTags from "../components/SEO/MetaTags";
import { pageConfigs } from "../utils/seo";

const Demo = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handlePlay = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  return (
    <>
      <MetaTags
        title={pageConfigs.demo.title}
        description={pageConfigs.demo.description}
        keywords={pageConfigs.demo.keywords}
        canonical={pageConfigs.demo.canonical}
      />
      
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#1e3d3b] via-[#2a5a56] to-[#1e3d3b]">
          <div className="max-w-5xl mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 tracking-tight text-white font-productSans">
                See{" "}
                <span className="bg-gradient-to-r from-[#619ed3] to-[#5c91a4] bg-clip-text text-transparent transition-all duration-300">
                  Notation
                </span>{" "}
                in Action
              </h1>
              <p className="text-xl text-white/80 max-w-3xl mx-auto leading-relaxed font-productSans">
                Watch how Notation transforms clinical documentation with AI-powered SOAP notes
              </p>
              <p className="mt-4 text-base text-white/60 italic font-productSans">
                Presented by <span className="font-medium text-white">Dr. Christina Rama, PT, DPT</span>
              </p>
            </motion.div>
          </div>
        </section>

        {/* Video Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            {/* Video Title */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-productSans">
                Experience Notation's Full Demo
              </h2>
              <p className="text-lg text-gray-600 font-productSans">
                See how real clinical sessions are transformed into complete SOAP notes
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <div className="bg-white/10 border border-white/20 backdrop-blur-xl overflow-hidden shadow-2xl group rounded-2xl relative">
                <div className="p-0 relative">
                  <video
                    ref={videoRef}
                    className="w-full h-full object-cover z-0 rounded-2xl"
                    controls
                    preload="auto"
                    playsInline
                    onPause={handlePause}
                  >
                    <source src="https://s3.us-east-1.amazonaws.com/fownd.care/Home+Videos/DemoV3_voice.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>

                  {/* Overlay Play Button */}
                  {!isPlaying && (
                    <div
                      className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/10 transition-opacity duration-300 rounded-2xl"
                      onClick={handlePlay}
                    >
                      <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <Play className="w-10 h-10 text-white ml-1" />
              </div>
                </div>
              )}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 font-productSans">
                Ready to experience{" "}
                <span className="bg-gradient-to-r from-[#619ed3] to-[#5c91a4] bg-clip-text text-transparent">
                  Notation
                </span>
                {" "}yourself?
              </h2>
              <p className="text-lg text-gray-600 mb-12 max-w-2xl mx-auto font-dmSans">
                Join hundreds of clinicians who have transformed their documentation workflow. 
                Start documenting smarter, not harder.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                <a
                  href="https://notation.fownd.care/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group"
                >
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-[#619ed3] text-white px-8 py-4 text-lg rounded-full font-semibold hover:bg-[#5c91a4] transition-all duration-300 shadow-lg hover:shadow-xl transform flex items-center gap-3"
                  >
                    <Sparkles className="w-5 h-5" />
                    Start Notation Free
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                  </motion.button>
                </a>
                
                <a
                  href="https://bookings.cloud.microsoft/book/NotationAI@fownd.org/?ismsaljsauthenabled"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group"
                >
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-white text-[#619ed3] border-2 border-[#619ed3] px-8 py-4 text-lg rounded-full font-semibold hover:bg-[#619ed3] hover:text-white transition-all duration-300 shadow-lg hover:shadow-xl transform flex items-center gap-3"
                  >
                    <Users className="w-5 h-5" />
                    Schedule Personal Demo
                  </motion.button>
                </a>
              </div>

              <p className="text-gray-500 text-sm mt-6">
                Instant Access • No Sign-Up • HIPAA Compliant
              </p>
            </motion.div>
                </div>
        </section>

        {/* Features Highlight Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-white">
          <div className="max-w-6xl mx-auto">
                         <motion.div
               initial={{ opacity: 0, y: 50 }}
               whileInView={{ opacity: 1, y: 0 }}
               viewport={{ once: true }}
               transition={{ duration: 0.8 }}
               className="text-center mb-16"
             >
               <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 font-productSans">
                 Why Choose Notation?
               </h2>
               <p className="text-lg text-gray-600 max-w-2xl mx-auto font-productSans">
                 Built by clinicians, for clinicians. Experience the future of clinical documentation.
               </p>
             </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: "🎤",
                  title: "Real-Time Listening",
                  description: "AI captures your conversation and generates structured SOAP notes instantly"
                },
                {
                  icon: "⚡",
                  title: "Instant Documentation",
                  description: "No more post-session admin work. Notes are ready when your session ends"
                },
                {
                  icon: "🔒",
                  title: "HIPAA Compliant",
                  description: "Enterprise-grade security ensures patient data is always protected"
                }
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl hover:border-[#619ed3]/20 transition-all duration-300 text-center group"
                >
                  <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-productSans">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 font-dmSans">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Demo;
